import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtMXN } from "../lib/api";
import Topbar from "../components/Topbar";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Plus, Trash2, ArrowLeft, Upload, FileText, Image, X, FileCode2, AlertTriangle } from "lucide-react";
import { motion } from "framer-motion";

const EMPTY_ITEM = { description: "", quantity: 1, unit: "pza", unit_price: 0, total: 0, supply_id: null };

// Fix WH81 (2026-10-16): lee del XML (CFDI) lo que se muestra antes de enviar.
// Solo es una vista previa: el backend vuelve a validar el XML con el mismo
// parser de Facturas. Devuelve null si no es un CFDI timbrado.
function readCfdiPreview(xmlText) {
  try {
    const doc = new DOMParser().parseFromString(xmlText, "application/xml");
    if (doc.getElementsByTagName("parsererror").length) return null;
    const root = doc.documentElement;
    const tfd = doc.getElementsByTagNameNS("*", "TimbreFiscalDigital")[0];
    const emisor = doc.getElementsByTagNameNS("*", "Emisor")[0];
    const uuid = tfd ? (tfd.getAttribute("UUID") || tfd.getAttribute("uuid") || "") : "";
    const total = root.getAttribute("Total");
    if (!uuid || total === null) return null;
    return {
      uuid,
      total: Math.round(Number(total) * 100) / 100,
      rfc: emisor ? (emisor.getAttribute("Rfc") || emisor.getAttribute("rfc") || "").trim().toUpperCase() : "",
      nombre: emisor ? (emisor.getAttribute("Nombre") || emisor.getAttribute("nombre") || "").trim() : "",
    };
  } catch (_) {
    return null;
  }
}

function toText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsText(file);
  });
}

// Fix WH81 (2026-10-16): un 0 inicial se muestra vacío (con placeholder) para
// que al escribir el precio no quede "0150".
const shownNumber = (v) => (v === 0 ? "" : v);

function toBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function nowLocalDatetime() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function ExternalPurchaseNew() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const xmlRef = useRef(null);

  const [branches, setBranches] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [supplies, setSupplies] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [purchasedAt, setPurchasedAt] = useState(nowLocalDatetime());
  const [purchaseLocation, setPurchaseLocation] = useState("");
  const [supplierFreeText, setSupplierFreeText] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [reason, setReason] = useState("");
  const [items, setItems] = useState([{ ...EMPTY_ITEM }]);
  const [ivaRate, setIvaRate] = useState(16);
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  // Fix WH81 (2026-10-16): XML del CFDI opcional { name, data (data URL), info }.
  const [xmlFile, setXmlFile] = useState(null);
  const [busy, setBusy] = useState(false);

  // Calcular totales
  const subtotal = items.reduce((s, i) => s + (Number(i.total) || 0), 0);
  const iva = Math.round((subtotal * ivaRate) / 100 * 100) / 100;
  const total = Math.round((subtotal + iva) * 100) / 100;

  useEffect(() => {
    api.get("/branches").then((r) => {
      const all = r.data || [];
      setBranches(all);
      if (user?.role === "branch_manager") {
        const authorized = user.branches_authorized?.length
          ? user.branches_authorized
          : user.branch_id ? [user.branch_id] : [];
        setBranchId(authorized[0] || "");
      }
    }).catch(() => {});
    api.get("/suppliers").then((r) => setSuppliers(r.data || [])).catch(() => {});
    api.get("/supplies").then((r) => setSupplies(r.data || [])).catch(() => {});
  }, []);

  const updateItem = (idx, field, value) => {
    setItems((prev) => {
      const next = prev.map((it, i) => i === idx ? { ...it, [field]: value } : it);
      if (field === "quantity" || field === "unit_price") {
        const it = next[idx];
        next[idx].total = Math.round(Number(it.quantity) * Number(it.unit_price) * 100) / 100;
      }
      // Fix WH81 (2026-10-16): también se puede escribir el total de la línea
      // (como viene en el ticket) y el precio unitario se calcula solo.
      if (field === "total") {
        const qty = Number(next[idx].quantity);
        next[idx].unit_price = qty > 0 ? Math.round((Number(value) / qty) * 10000) / 10000 : 0;
      }
      if (field === "supply_id" && value) {
        const sup = supplies.find((s) => s.id === value);
        if (sup) {
          next[idx].description = sup.name;
          next[idx].unit = sup.unit || "";
        }
      }
      return next;
    });
  };

  const addItem = () => setItems((p) => [...p, { ...EMPTY_ITEM }]);
  const removeItem = (idx) => setItems((p) => p.filter((_, i) => i !== idx));

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    // Fix WH81 (2026-10-16): limpiar el input para poder volver a elegir el mismo archivo.
    e.target.value = "";
    if (!file) return;
    const allowed = ["application/pdf", "image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"];
    if (!allowed.includes(file.type) && !file.name.match(/\.(pdf|jpg|jpeg|png|webp|heic|heif)$/i)) {
      toast.error("Formato no permitido. Sube un PDF, JPG, PNG, WebP o HEIC.");
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast.error("El archivo pesa más de 15 MB.");
      return;
    }
    const b64 = await toBase64(file);
    setReceiptFile({ name: file.name, data: b64, type: file.type });
    if (file.type.startsWith("image/")) {
      setReceiptPreview(b64);
    } else {
      setReceiptPreview(null);
    }
  };

  // Fix WH81 (2026-10-16): XML del CFDI opcional (Cristian: "no me da la opción
  // de subir el archivo .xml").
  const handleXmlChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.name.match(/\.xml$/i)) {
      toast.error("Sube el archivo .xml de la factura.");
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      toast.error("El XML pesa más de 4 MB.");
      return;
    }
    const info = readCfdiPreview(await toText(file));
    if (!info) {
      toast.error("Ese XML no es un CFDI timbrado (no tiene UUID o Total).");
      return;
    }
    const data = await toBase64(file);
    setXmlFile({ name: file.name, data, info });
    if (!supplierFreeText.trim() && info.nombre) setSupplierFreeText(info.nombre);
    if (!supplierId && info.rfc) {
      const s = suppliers.find((x) => (x.rfc || "").toUpperCase() === info.rfc);
      if (s) setSupplierId(s.id);
    }
  };
  const xmlTotalMismatch = !!xmlFile && Math.abs(xmlFile.info.total - total) > 0.05;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!branchId) { toast.error("Selecciona la sucursal"); return; }
    if (!purchaseLocation.trim()) { toast.error("Indica el lugar de la compra"); return; }
    if (!supplierFreeText.trim()) { toast.error("Indica el nombre del proveedor"); return; }
    if (reason.trim().length < 20) { toast.error("El motivo debe tener al menos 20 caracteres"); return; }
    if (items.some((it) => !it.description.trim())) { toast.error("Todos los items deben tener descripción"); return; }
    if (!receiptFile) { toast.error("El recibo/factura es obligatorio"); return; }

    setBusy(true);
    try {
      const payload = {
        branch_id: branchId,
        purchased_at: new Date(purchasedAt).toISOString(),
        supplier_name_free_text: supplierFreeText.trim(),
        supplier_id: supplierId || null,
        purchase_location: purchaseLocation.trim(),
        reason: reason.trim(),
        items_free_text: items.map((it) => ({
          description: it.description,
          quantity: Number(it.quantity),
          unit: it.unit,
          unit_price: Number(it.unit_price),
          total: Number(it.total),
          supply_id: it.supply_id || null,
        })),
        subtotal,
        iva,
        total,
        receipt_filename: receiptFile.name,
        receipt_base64: receiptFile.data,
      };
      // Fix WH81 (2026-10-16): el XML es opcional; sin XML el payload queda igual que antes.
      if (xmlFile) {
        payload.xml_filename = xmlFile.name;
        payload.xml_base64 = xmlFile.data;
      }
      const res = await api.post("/purchase-orders/external", payload);
      toast.success(`Compra externa ${res.data.folio} enviada para aprobación`);
      navigate(`/ocs/${res.data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al enviar la compra externa");
    } finally {
      setBusy(false);
    }
  };

  const isBranchLocked = user?.role === "branch_manager";
  const branchName = branches.find((b) => b.id === branchId)?.name || "";

  return (
    <>
      <Topbar title="Cargar compra externa" subtitle="Compra de emergencia sin OC formal" />
      <div className="p-6 max-w-3xl space-y-5">
        <button
          onClick={() => navigate(-1)}
          className="text-sm font-semibold text-slate-500 hover:text-[#1A1A1A] flex items-center gap-1.5"
        >
          <ArrowLeft className="w-4 h-4" /> Volver
        </button>

        {/* Banner informativo */}
        <div className="bg-amber-50 border border-amber-300 rounded-2xl px-5 py-4">
          <p className="text-sm font-semibold text-amber-900">
            Esta compra va a requerir VoBo de Compras y luego de Direccion antes de proceder al reintegro.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Sucursal */}
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
            <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider">Datos generales</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Sucursal *</label>
                {isBranchLocked ? (
                  <div
                    data-testid="ext-branch-display"
                    className="px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-semibold text-slate-700"
                  >
                    {branchName || "Cargando..."}
                  </div>
                ) : (
                  <select
                    data-testid="ext-branch-select"
                    value={branchId}
                    onChange={(e) => setBranchId(e.target.value)}
                    required
                    className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
                  >
                    <option value="">Seleccionar sucursal</option>
                    {branches.map((b) => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </select>
                )}
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Fecha y hora de la compra *</label>
                <input
                  data-testid="ext-purchased-at"
                  type="datetime-local"
                  value={purchasedAt}
                  onChange={(e) => setPurchasedAt(e.target.value)}
                  required
                  className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Lugar de la compra *</label>
              <input
                data-testid="ext-purchase-location"
                type="text"
                value={purchaseLocation}
                onChange={(e) => setPurchaseLocation(e.target.value)}
                placeholder="Ej: Walmart Hermosillo Av. Lopez Portillo, Hermosillo, Son."
                required
                className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
              />
            </div>
          </motion.div>

          {/* Proveedor */}
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
            <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider">Proveedor</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Nombre del proveedor *</label>
                <input
                  data-testid="ext-supplier-free-text"
                  type="text"
                  value={supplierFreeText}
                  onChange={(e) => setSupplierFreeText(e.target.value)}
                  placeholder="Ej: Walmart, La panaderia de la esquina..."
                  required
                  className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Proveedor registrado (opcional)</label>
                <select
                  data-testid="ext-supplier-id"
                  value={supplierId}
                  onChange={(e) => {
                    setSupplierId(e.target.value);
                    if (e.target.value) {
                      const s = suppliers.find((s) => s.id === e.target.value);
                      if (s && !supplierFreeText) setSupplierFreeText(s.business_name);
                    }
                  }}
                  className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
                >
                  <option value="">— Sin vincular —</option>
                  {suppliers.map((s) => (
                    <option key={s.id} value={s.id}>{s.business_name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Motivo de la compra de emergencia *</label>
              <textarea
                data-testid="ext-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                placeholder="Minimo 20 caracteres. Ej: Se acabo la masa pizzera en hora pico, no habia stock en bodega..."
                required
                className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00] resize-none"
              />
              <div className={`text-xs mt-1 ${reason.trim().length < 20 ? "text-red-500" : "text-slate-400"}`}>
                {reason.trim().length}/20 min
              </div>
            </div>
          </motion.div>

          {/* Items */}
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider">Items comprados</h3>
              <button
                type="button"
                data-testid="ext-add-item-btn"
                onClick={addItem}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[#FFDF00] hover:bg-yellow-400 text-[#1A1A1A] rounded-lg text-xs font-bold transition"
              >
                <Plus className="w-3.5 h-3.5" /> Agregar item
              </button>
            </div>
            {/* Fix WH81 (2026-10-16): Cristian pidió poder editar el precio. Ya se
                podía, pero en el celular la columna quedaba fuera de la pantalla y el 0
                inicial confundía. Ahora cada item muestra sus campos con nombre en el
                celular y el total de la línea también se puede escribir. */}
            <p data-testid="ext-items-hint" className="text-xs text-slate-500 -mt-2">
              Escribe el precio unitario o el total de la línea como viene en el ticket; el otro se calcula solo.
            </p>
            <div className="text-sm">
              <div className="hidden sm:grid sm:grid-cols-[minmax(0,1fr)_5rem_5rem_7rem_7rem_2rem] gap-3 pb-2 border-b border-slate-200 text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                <div>Descripcion</div>
                <div className="text-right">Cant.</div>
                <div>Unidad</div>
                <div className="text-right">P. Unit.</div>
                <div className="text-right">Total</div>
                <div></div>
              </div>
              <div>
                  {items.map((item, idx) => (
                    <div key={idx} data-testid={`ext-item-row-${idx}`} className="grid grid-cols-2 sm:grid-cols-[minmax(0,1fr)_5rem_5rem_7rem_7rem_2rem] gap-x-3 gap-y-2 py-3 border-b border-slate-100 last:border-0 items-start">
                      <div className="col-span-2 sm:col-span-1">
                        <label className="sm:hidden block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">Descripcion</label>
                        <input
                          data-testid={`ext-item-desc-${idx}`}
                          value={item.description}
                          onChange={(e) => updateItem(idx, "description", e.target.value)}
                          placeholder="Descripcion del producto"
                          required
                          className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#FFDF00]"
                        />
                        {supplies.length > 0 && (
                          <select
                            data-testid={`ext-item-supply-${idx}`}
                            value={item.supply_id || ""}
                            onChange={(e) => updateItem(idx, "supply_id", e.target.value || null)}
                            className="w-full mt-1 px-2 py-1 border border-slate-100 rounded-lg text-xs text-slate-500 focus:outline-none focus:ring-1 focus:ring-[#FFDF00] bg-slate-50"
                          >
                            <option value="">Vincular a catalogo (opcional)</option>
                            {supplies.map((s) => (
                              <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                          </select>
                        )}
                      </div>
                      <div>
                        <label className="sm:hidden block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">Cantidad</label>
                        <input
                          data-testid={`ext-item-qty-${idx}`}
                          type="number"
                          min="0.01"
                          step="0.01"
                          value={item.quantity}
                          onChange={(e) => updateItem(idx, "quantity", e.target.value)}
                          className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-sm text-right focus:outline-none focus:ring-1 focus:ring-[#FFDF00]"
                        />
                      </div>
                      <div>
                        <label className="sm:hidden block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">Unidad</label>
                        <input
                          data-testid={`ext-item-unit-${idx}`}
                          value={item.unit}
                          onChange={(e) => updateItem(idx, "unit", e.target.value)}
                          placeholder="pza"
                          className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#FFDF00]"
                        />
                      </div>
                      <div>
                        <label className="sm:hidden block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">Precio unitario</label>
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">$</span>
                          <input
                            data-testid={`ext-item-price-${idx}`}
                            type="number"
                            inputMode="decimal"
                            min="0"
                            step="0.01"
                            value={shownNumber(item.unit_price)}
                            onChange={(e) => updateItem(idx, "unit_price", e.target.value)}
                            placeholder="0.00"
                            className="w-full pl-5 pr-2 py-1.5 border border-slate-200 rounded-lg text-sm text-right font-mono focus:outline-none focus:ring-1 focus:ring-[#FFDF00]"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="sm:hidden block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">Total</label>
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">$</span>
                          <input
                            data-testid={`ext-item-total-${idx}`}
                            type="number"
                            inputMode="decimal"
                            min="0"
                            step="0.01"
                            value={shownNumber(item.total)}
                            onChange={(e) => updateItem(idx, "total", e.target.value)}
                            placeholder="0.00"
                            className="w-full pl-5 pr-2 py-1.5 border border-slate-200 rounded-lg text-sm text-right font-mono font-bold text-slate-700 focus:outline-none focus:ring-1 focus:ring-[#FFDF00]"
                          />
                        </div>
                      </div>
                      <div className="col-span-2 sm:col-span-1 flex justify-end sm:pt-1">
                        {items.length > 1 && (
                          <button
                            type="button"
                            data-testid={`ext-remove-item-${idx}`}
                            onClick={() => removeItem(idx)}
                            className="p-1 text-slate-400 hover:text-red-500 transition"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            </div>

            {/* Totales */}
            <div className="flex justify-end pt-3 border-t border-slate-200">
              <div className="space-y-1.5 min-w-[240px]">
                <div className="flex justify-between text-sm text-slate-500">
                  <span>Subtotal</span>
                  <span className="font-mono">{fmtMXN(subtotal)}</span>
                </div>
                <div className="flex justify-between text-sm text-slate-500 items-center gap-2">
                  <span>IVA</span>
                  <div className="flex items-center gap-1">
                    <input
                      data-testid="ext-iva-rate"
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      value={ivaRate}
                      onChange={(e) => setIvaRate(Number(e.target.value))}
                      className="w-14 px-2 py-0.5 border border-slate-200 rounded-lg text-sm text-right font-mono focus:outline-none focus:ring-1 focus:ring-[#FFDF00]"
                    />
                    <span className="text-xs text-slate-400">%</span>
                    <span className="font-mono">{fmtMXN(iva)}</span>
                  </div>
                </div>
                <div className="flex justify-between text-lg font-display font-extrabold text-[#1A1A1A] pt-2 border-t border-slate-200">
                  <span>Total</span>
                  <span data-testid="ext-total" className="font-mono">{fmtMXN(total)}</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Recibo */}
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
            <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider">Recibo o factura *</h3>
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif"
              onChange={handleFileChange}
              className="hidden"
            />
            {!receiptFile ? (
              <button
                type="button"
                data-testid="ext-upload-receipt-btn"
                onClick={() => fileRef.current?.click()}
                className="w-full border-2 border-dashed border-slate-300 hover:border-[#FFDF00] rounded-2xl p-8 flex flex-col items-center gap-3 text-slate-500 hover:text-[#1A1A1A] transition-colors"
              >
                <Upload className="w-8 h-8" />
                <div className="text-sm font-semibold">Subir recibo o factura</div>
                <div className="text-xs text-slate-400">PDF, JPG, PNG, WebP, HEIC — max. 15 MB</div>
              </button>
            ) : (
              <div
                data-testid="ext-receipt-preview"
                className="border border-slate-200 rounded-2xl p-4 flex items-center gap-4"
              >
                {receiptPreview ? (
                  <img src={receiptPreview} alt="preview" className="w-16 h-16 object-cover rounded-xl border" />
                ) : (
                  <div className="w-16 h-16 bg-slate-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <FileText className="w-7 h-7 text-slate-500" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-700 truncate">{receiptFile.name}</div>
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    className="text-xs text-blue-600 hover:underline mt-0.5"
                  >
                    Cambiar archivo
                  </button>
                </div>
                <button
                  type="button"
                  data-testid="ext-remove-receipt-btn"
                  onClick={() => { setReceiptFile(null); setReceiptPreview(null); }}
                  className="p-1.5 text-slate-400 hover:text-red-500 transition"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Fix WH81 (2026-10-16): XML del CFDI, opcional */}
            <div className="pt-4 border-t border-slate-100 space-y-2">
              <div className="text-xs font-semibold text-slate-600">XML de la factura (opcional)</div>
              <div className="text-xs text-slate-400">Si te dieron factura, sube también el XML. Se usa para registrar el UUID y comparar el total.</div>
              <input
                ref={xmlRef}
                type="file"
                accept=".xml,text/xml,application/xml"
                onChange={handleXmlChange}
                className="hidden"
              />
              {!xmlFile ? (
                <button
                  type="button"
                  data-testid="ext-upload-xml-btn"
                  onClick={() => xmlRef.current?.click()}
                  className="w-full border-2 border-dashed border-slate-200 hover:border-[#FFDF00] rounded-2xl p-4 flex items-center justify-center gap-2 text-sm font-semibold text-slate-500 hover:text-[#1A1A1A] transition-colors"
                >
                  <FileCode2 className="w-5 h-5" /> Subir XML (.xml) — máx. 4 MB
                </button>
              ) : (
                <div data-testid="ext-xml-preview" className="border border-slate-200 rounded-2xl p-4 space-y-2">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <FileCode2 className="w-5 h-5 text-slate-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-slate-700 truncate">{xmlFile.name}</div>
                      <div className="text-[11px] text-slate-500 font-mono truncate">UUID {xmlFile.info.uuid}</div>
                      <div className="text-[11px] text-slate-500 truncate">
                        {xmlFile.info.nombre || "—"}{xmlFile.info.rfc ? ` · ${xmlFile.info.rfc}` : ""} · Total XML <span className="font-mono">{fmtMXN(xmlFile.info.total)}</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      data-testid="ext-remove-xml-btn"
                      onClick={() => setXmlFile(null)}
                      className="p-1.5 text-slate-400 hover:text-red-500 transition"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  {xmlTotalMismatch && (
                    <div data-testid="ext-xml-total-warning" className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <span>El total del XML ({fmtMXN(xmlFile.info.total)}) no coincide con el total capturado ({fmtMXN(total)}). Revisa los precios o el IVA.</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </motion.div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-5 py-3 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition"
            >
              Cancelar
            </button>
            <button
              type="submit"
              data-testid="ext-submit-btn"
              disabled={busy}
              className="flex-1 py-3 bg-[#1A1A1A] hover:bg-slate-800 text-white rounded-xl text-sm font-bold disabled:opacity-50 transition"
            >
              {busy ? "Enviando..." : "Enviar para aprobacion"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
